import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useMsal } from '@azure/msal-react';

const IDLE_TIMEOUT = 1000 * 60 * 119; // 119 minutes
const WARNING_DURATION = 60; // seconds
const LAST_ACTIVITY_KEY = 'lastActivityTime';

const useIdleLogout = () => {
  const { instance: msalInstance } = useMsal();
  const [showWarning, setShowWarning] = useState(false);
  const [remaining, setRemaining] = useState(WARNING_DURATION);
  const countdownRef = useRef(null);

  const isAuthenticated = msalInstance.getAllAccounts().length > 0;

  const handleLogOut = useCallback(() => {
    localStorage.clear();
    window.location.href = '/';
  }, []);

  const onIdle = () => {
    if (!isAuthenticated) return;
    setShowWarning(true);
    setRemaining(WARNING_DURATION);

    countdownRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          handleLogOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const { reset } = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle,
    debounce: 500
  });

  const stayLoggedIn = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

    reset();
    setShowWarning(false);
    clearInterval(countdownRef.current);
  };

  useEffect(() => {
    const onStorageChange = (e) => {
      if (e.key === LAST_ACTIVITY_KEY) {
        reset(); // activity from another tab — reset this tab's timer
        setShowWarning(false);
        clearInterval(countdownRef.current);
      }
    };

    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, [reset]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const updateLastActivityTime = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    };

    const handleVisibilityChange = () => {
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10);
      const idleTime = Date.now() - lastActivity;
      if (document.visibilityState === 'visible' && idleTime > IDLE_TIMEOUT) {
        handleLogOut();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    ['mousemove', 'keydown', 'click'].forEach((event) =>
      window.addEventListener(event, updateLastActivityTime)
    );

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      ['mousemove', 'keydown', 'click'].forEach((event) =>
        window.removeEventListener(event, updateLastActivityTime)
      );
    };
  }, [handleLogOut, isAuthenticated]);

  // Handle browser close & reopen
  useEffect(() => {
    const now = Date.now();
    const sessionMarker = sessionStorage.getItem('tabActive');

    const checkAndLogout = () => {
      if (!sessionMarker) {
        const lastSessionTime = localStorage.getItem('lastSessionTime');
        if (!lastSessionTime || now - Number(lastSessionTime) > IDLE_TIMEOUT) {
          handleLogOut();
        }
      }
    };

    checkAndLogout();

    sessionStorage.setItem('tabActive', 'true');
    localStorage.setItem('lastSessionTime', now.toString());

    const handleUnload = () => {
      localStorage.setItem('lastSessionTime', Date.now().toString());
    };

    window.addEventListener('unload', handleUnload);
    return () => window.removeEventListener('unload', handleUnload);
  }, [handleLogOut]);

  return {
    showWarning,
    remaining,
    stayLoggedIn,
    isAuthenticated
  };
};

export default useIdleLogout;